import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Link as MuiLink,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import LaunchIcon from '@mui/icons-material/Launch';
import { Link as RouterLink } from 'react-router-dom';

const CarouselSection = ({
  title = 'Latest News',
  items = [],
  backgroundStyle = 'white',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [contentHeight, setContentHeight] = useState(null);
  const measureRef = useRef(null);

  const displayItems = items.slice(0, 5); // maximum 5 items

  // Always run hooks before any return
  useEffect(() => {
    if (!measureRef.current) return;

    const contentBlocks = measureRef.current.querySelectorAll('.text-content');
    let maxHeight = 0;

    contentBlocks.forEach((block) => {
      const height = block.scrollHeight;
      if (height > maxHeight) maxHeight = height;
    });

    setContentHeight(maxHeight-16);
  }, [items]);

  if (!displayItems || displayItems.length === 0) return null;

  const handlePrev = () => {
    setActiveIndex((prev) =>
      prev === 0 ? displayItems.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setActiveIndex((prev) =>
      prev === displayItems.length - 1 ? 0 : prev + 1
    );
  };

  const activeItem = displayItems[activeIndex]; // use displayItems

  const isExternal = activeItem.type === 'external';
  const linkTarget = isExternal ? activeItem.extLink : activeItem.link;

  const bgStyles =
    backgroundStyle === 'grey'
      ? {
          backgroundColor: 'rgba(20,36,52,0.05)',
          borderTop: '0.5px solid #142434',
          borderBottom: '0.5px solid #142434',
        }
      : {
          backgroundColor: '#ffffff',
        };

  return (
    <Box component="section" sx={{ py: 6, textAlign: 'center', ...bgStyles }}>

      {/* Hidden measurement container */}
      <Box
        ref={measureRef}
        sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
      >
        {displayItems.map((item, i) => (
          <Box key={i} className="text-content" sx={{ width: 600, p: 0 }}>
            <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 24 }}>
              {item.title}
            </Typography>
            {item.date && (
              <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 14 }}>
                {item.date}
              </Typography>
            )}
            <Typography sx={{ fontFamily: 'PT Sans, sans-serif', fontSize: 16, lineHeight: 1.5 }}>
              {item.description}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 400,
          fontSize: '32px',
          color: '#142434',
          mb: 4,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <IconButton
          aria-label="previous"
          onClick={handlePrev}
          sx={{
            width: 40,
            height: 80,
            borderRadius: 2,
            '&:hover': { backgroundColor: 'rgba(72,149,150,0.1)' },
          }}
        >
          <ChevronLeft sx={{ fontSize: 48, color: '#489596' }} />
        </IconButton>

        <Card
          sx={{
            display: 'flex',
            maxWidth: 900,
            width: '100%',
            border: '0.5px solid #489596',
            boxShadow: 'none',
            borderRadius: 2,
            p: 3,
            backgroundColor: '#fff',
          }}
        >
          {activeItem.image ? (
            <MuiLink
              component={isExternal ? 'a' : RouterLink}
              href={isExternal ? linkTarget : undefined}
              to={!isExternal ? linkTarget : undefined}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              sx={{ display: 'block', width: 244, height: 244, flexShrink: 0, mr: 3 }}
            >
              <CardMedia
                component="img"
                image={activeItem.image}
                alt={activeItem.title}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </MuiLink>
          ) : (
            <Box sx={{ width: 244, height: 244, backgroundColor: '#f0f0f0', mr: 3 }} />
          )}

          <CardContent
            className="text-content"
            sx={{
              p: 0,
              textAlign: 'left', // preserve original alignment
              minHeight: contentHeight ? `${contentHeight}px` : 'auto', // uniform height
            }}
          >
            <MuiLink
              component={isExternal ? 'a' : RouterLink}
              href={isExternal ? linkTarget : undefined}
              to={!isExternal ? linkTarget : undefined}
              underline="none"
              sx={{ textDecoration: 'none', display: 'inline-block', mb: 1 }}
            >
              <Typography
                sx={{
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: 24,
                  color: '#489596',
                  display: 'inline',
                }}
              >
                {activeItem.title}
                {isExternal && (
                  <LaunchIcon
                    sx={{
                      fontSize: 18,
                      ml: '2px',
                      verticalAlign: 'baseline',
                      position: 'relative',
                      top: '1px',
                      color: '#489596',
                    }}
                  />
                )}
              </Typography>
            </MuiLink>

            {activeItem.date && (
              <Typography
                sx={{
                  fontFamily: 'PT Sans, sans-serif',
                  fontSize: 14,
                  color: '#142434',
                  mb: 1,
                }}
              >
                {activeItem.date}
              </Typography>
            )}

            <Typography
              sx={{
                fontFamily: 'PT Sans, sans-serif',
                fontSize: 16,
                lineHeight: 1.5,
                color: '#142434',
                mb: 0,
              }}
            >
              {activeItem.description}
            </Typography>
          </CardContent>
        </Card>

        <IconButton
          aria-label="next"
          onClick={handleNext}
          sx={{
            width: 40,
            height: 80,
            borderRadius: 2,
            '&:hover': { backgroundColor: 'rgba(72,149,150,0.1)' },
          }}
        >
          <ChevronRight sx={{ fontSize: 48, color: '#489596' }} />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
        {displayItems.map((_, i) => (
          <Box
            key={i}
            onClick={() => setActiveIndex(i)}
            role="button"
            aria-label={`Go to slide ${i + 1}`}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              cursor: 'pointer',
              backgroundColor: i === activeIndex ? '#489596' : 'transparent',
              border: '1px solid #489596',
              transition: 'all 0.25s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CarouselSection;
